import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  CheckCircle, Loader2, XCircle, Calendar, Clock, MapPin,
  Receipt, Copy, CalendarPlus, Trophy, Users, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ── helpers ─────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return dt.toLocaleDateString('en-PH', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const p = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${(m || 0).toString().padStart(2, '0')} ${p}`;
}
function fmtPHP(v) {
  return `₱${Number(v || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function googleCalUrl({ title, subtitle, date, start, end, location }) {
  if (!date) return null;
  const [y, m, d] = date.split('-');
  const startTime = start || '09:00';
  const endTime = end || start || '10:00';
  const [sh, sm] = startTime.split(':');
  const [eh, em] = endTime.split(':');
  const s = `${y}${m}${d}T${sh.padStart(2, '0')}${(sm || '00').padStart(2, '0')}00`;
  const e = `${y}${m}${d}T${eh.padStart(2, '0')}${(em || '00').padStart(2, '0')}00`;
  const params = new URLSearchParams({
    action: 'TEMPLATE', text: title || 'AgriBooks', dates: `${s}/${e}`,
    details: subtitle || 'Show your QR check-in code when you arrive.',
    location: location || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// ── page ────────────────────────────────────────────────────────────────────
export default function SportsPayReturnPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnStatus = params.get('status');
  const type = params.get('type') || 'booking';   // booking | open_play | tournament
  const bookingId = params.get('booking');
  const sessionId = params.get('session');
  const playerId = params.get('player');
  const teamId = params.get('team');
  const src = params.get('src');            // staff-origin marker (walkin | bookings | open_play | tournament)

  const [state, setState] = useState(returnStatus === 'cancel' ? 'cancelled' : 'checking');
  const [receipt, setReceipt] = useState(null);

  // Resolve the polling URL based on the entity type
  const statusUrl = useMemo(() => {
    if (type === 'open_play' && sessionId && playerId) {
      return `${BACKEND}/api/sports/public/${slug}/open-play/${sessionId}/players/${playerId}/pay/status`;
    }
    if (type === 'tournament' && teamId) {
      // Tournament id isn't in the URL — the status endpoint resolves it via team. We embed tid=(auto) below.
      return null;   // handled specially below
    }
    if (type === 'booking' && bookingId) {
      return `${BACKEND}/api/sports/public/${slug}/pay/status/${bookingId}`;
    }
    return null;
  }, [type, slug, bookingId, sessionId, playerId, teamId]);

  // For tournament, we need to look up the tournament id from the team first.
  // Since the tournament checkout URL always carries teamId only, we can call a
  // helper that scans the tenant's tournaments. For now, the FE re-uses a
  // convention: the backend `/tournaments/{tid}/teams/{team_id}` requires tid,
  // so we fetch it via a lightweight tournament-list call.
  const [tournamentId, setTournamentId] = useState(null);
  useEffect(() => {
    if (type !== 'tournament' || !teamId || tournamentId) return;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/sports/public/${slug}/tournaments/lookup-team/${teamId}`);
        setTournamentId(r.data?.tournament_id);
      } catch { /* fall through — polling will start once tid resolves */ }
    })();
  }, [type, teamId, tournamentId, slug]);

  const tournamentStatusUrl = (type === 'tournament' && tournamentId && teamId)
    ? `${BACKEND}/api/sports/public/${slug}/tournaments/${tournamentId}/teams/${teamId}/pay/status`
    : null;

  const effectiveUrl = statusUrl || tournamentStatusUrl;

  // Context-aware exit: owner-side payment-link endpoints tag their return URLs
  // with &src=…. A customer's phone opens the same URL but carries no sports
  // token, so it keeps the public flow — only a logged-in staff device is
  // routed back to where checkout started (finance/money desk for events).
  const staffBack = useMemo(() => {
    if (!src || !localStorage.getItem('sports_token')) return null;
    if (src === 'walkin') return { to: '/sports/walk-in', label: 'Back to Walk-In' };
    if (src === 'bookings') return { to: '/sports/bookings', label: 'Back to Bookings' };
    if (src === 'open_play') {
      return { to: sessionId ? `/sports/open-play/${sessionId}?tab=players` : '/sports/tournaments', label: 'Back to Money Desk' };
    }
    if (src === 'tournament') {
      return { to: tournamentId ? `/sports/tournaments/${tournamentId}?tab=players` : '/sports/tournaments', label: 'Back to Money Desk' };
    }
    return null;
  }, [src, sessionId, tournamentId]);

  useEffect(() => {
    if (returnStatus === 'cancel') return;
    if (!effectiveUrl) return;
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const r = await axios.get(effectiveUrl);
        if (r.data?.paid) {
          if (!stop) { setReceipt({ type, ...r.data }); setState('paid'); }
          return;
        }
      } catch (_) { /* transient — keep polling */ }
      tries += 1;
      if (tries >= 8) { if (!stop) setState('pending'); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [effectiveUrl, returnStatus, type]);

  // ── derived receipt fields per type ─────────────────────────────────────
  const view = useMemo(() => {
    if (!receipt) return null;
    if (receipt.type === 'open_play') {
      return {
        title: 'You\'re in the queue!',
        subtitle: `${receipt.player_name || 'Player'} — locked in for ${receipt.session_name} at ${receipt.facility_name}.`,
        headerIcon: <Zap className="h-14 w-14" style={{ color: '#00E676' }} />,
        detailIcon: <Users className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />,
        detailLabel: 'Session',
        detailValue: `${receipt.session_name}${receipt.division_name ? ` · ${receipt.division_name}` : ''}${receipt.session_format ? ` · ${receipt.session_format}` : ''}`,
        secondaryIcon: <Trophy className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />,
        secondaryLabel: 'Sport',
        secondaryValue: receipt.sport || '—',
        calendarTitle: `${receipt.session_name} — Open Play`,
      };
    }
    if (receipt.type === 'tournament') {
      return {
        title: 'You\'re registered!',
        subtitle: `Team ${receipt.team_name}${receipt.captain_name ? ` (captain: ${receipt.captain_name})` : ''} · ${receipt.tournament_name} @ ${receipt.facility_name}`,
        headerIcon: <Trophy className="h-14 w-14" style={{ color: '#00E676' }} />,
        detailIcon: <Users className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />,
        detailLabel: 'Tournament',
        detailValue: `${receipt.tournament_name}${receipt.division_name ? ` · ${receipt.division_name}` : ''}`,
        secondaryIcon: <Zap className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />,
        secondaryLabel: 'Format',
        secondaryValue: `${receipt.tournament_format || '—'}${receipt.tournament_sport ? ` · ${receipt.tournament_sport}` : ''}`,
        calendarTitle: `${receipt.tournament_name} — ${receipt.team_name}`,
      };
    }
    // default = booking
    return {
      title: 'See you on the court!',
      subtitle: `${receipt.customer?.name ? receipt.customer.name + ' — ' : ''}your slot at ${receipt.facility_name} is locked in.`,
      headerIcon: <CheckCircle className="h-14 w-14" style={{ color: '#00E676' }} />,
      detailIcon: <MapPin className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />,
      detailLabel: 'Court',
      detailValue: receipt.court_name || '—',
      secondaryIcon: null,
      calendarTitle: `${receipt.court_name || 'Court'} — ${receipt.facility_name}`,
    };
  }, [receipt]);

  const checkinUrl = receipt?.checkin_url ? `${window.location.origin}${receipt.checkin_url}` : '';
  const calUrl = useMemo(() => receipt && view && googleCalUrl({
    title: view.calendarTitle, subtitle: view.subtitle,
    date: receipt.date, start: receipt.start_time, end: receipt.end_time,
    location: receipt.facility_name,
  }), [receipt, view]);

  const copyCheckin = () => {
    if (!checkinUrl) return;
    navigator.clipboard.writeText(checkinUrl)
      .then(() => toast.success('Check-in link copied'))
      .catch(() => toast.error('Copy failed — long-press the QR to save instead'));
  };

  // ── styling primitives ───────────────────────────────────────────────────
  const page = { minHeight: '100vh', background: '#0A0A0A', color: '#fff', padding: '32px 16px', fontFamily: 'Manrope, sans-serif' };
  const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 20, padding: 28, maxWidth: 480, margin: '0 auto' };
  const chip = { fontSize: 10, fontWeight: 900, letterSpacing: 1, padding: '4px 10px', borderRadius: 999, background: '#00E67618', color: '#00E676', textTransform: 'uppercase' };
  const row = { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderTop: '1px dashed #232323' };
  const label = { fontSize: 11, letterSpacing: 1, color: '#737373', textTransform: 'uppercase', fontWeight: 700 };
  const value = { fontSize: 15, color: '#fff', fontWeight: 600, marginTop: 2, wordBreak: 'break-word' };
  const btnPrimary = { flex: 1, background: '#E63946', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 18px', fontWeight: 800, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.6, cursor: 'pointer', fontSize: 13 };
  const btnGhost = { flex: 1, background: '#1A1A1A', color: '#a3a3a3', border: '1px solid #2A2A2A', borderRadius: 12, padding: '13px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 };

  // ── states ───────────────────────────────────────────────────────────────
  if (state === 'checking') {
    return (
      <div style={page} data-testid="sports-pay-return-page">
        <div style={{ ...card, textAlign: 'center', padding: '56px 28px' }}>
          <Loader2 className="h-12 w-12 mx-auto mb-5 animate-spin" style={{ color: '#E63946' }} />
          <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 900 }}>Confirming payment…</h1>
          <p style={{ color: '#737373', marginTop: 10, fontSize: 14 }}>Hang tight — this usually takes a few seconds.</p>
        </div>
      </div>
    );
  }
  if (state === 'cancelled') {
    return (
      <div style={page} data-testid="sports-pay-return-page">
        <div style={{ ...card, textAlign: 'center' }}>
          <XCircle className="h-12 w-12 mx-auto mb-4" style={{ color: '#FF2D55' }} />
          <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 900 }}>Payment cancelled</h1>
          <p style={{ color: '#737373', marginTop: 10, fontSize: 14 }}>No charge was made. You can try again.</p>
          <button onClick={() => navigate(staffBack ? staffBack.to : `/book/${slug}`)} data-testid="pay-return-home-btn" style={{ ...btnPrimary, marginTop: 24, width: '100%' }}>{staffBack ? staffBack.label : 'Back to booking'}</button>
        </div>
      </div>
    );
  }
  if (state === 'pending') {
    return (
      <div style={page} data-testid="sports-pay-return-page">
        <div style={{ ...card, textAlign: 'center' }}>
          <Loader2 className="h-10 w-10 mx-auto mb-4" style={{ color: '#FFB300' }} />
          <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 800 }}>Still processing</h1>
          <p style={{ color: '#737373', marginTop: 10, fontSize: 14 }}>If you completed payment, it may take a minute to reflect. You can safely close this page — you&apos;ll receive a confirmation email once it clears.</p>
          <button onClick={() => navigate(staffBack ? staffBack.to : `/book/${slug}`)} data-testid="pay-return-home-btn" style={{ ...btnPrimary, marginTop: 24, width: '100%' }}>{staffBack ? staffBack.label : 'Back to booking'}</button>
        </div>
      </div>
    );
  }

  // ── PAID: full receipt ───────────────────────────────────────────────────
  const isDeposit = receipt?.payment_status === 'deposit_paid' || receipt?.pay_kind === 'deposit';

  return (
    <div style={page} data-testid="sports-pay-return-page">
      <div style={card}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={chip}>Confirmed</span>
            {isDeposit && <span style={{ ...chip, background: '#FFB30018', color: '#FFB300' }}>Deposit paid</span>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            {view?.headerIcon}
          </div>
          <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900 }} data-testid="pay-return-success">
            {view?.title}
          </h1>
          <p style={{ color: '#a3a3a3', marginTop: 6, fontSize: 13 }}>{view?.subtitle}</p>
        </div>

        {/* QR check-in */}
        <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 16, padding: 18, textAlign: 'center', marginBottom: 20 }}>
          <p style={{ ...label, marginBottom: 12 }}>Check-in QR</p>
          <div style={{ display: 'inline-block', background: '#fff', padding: 10, borderRadius: 12 }} data-testid="pay-return-qr-wrap">
            {checkinUrl && <QRCodeSVG value={checkinUrl} size={160} bgColor="#ffffff" fgColor="#0A0A0A" level="M" />}
          </div>
          <p style={{ color: '#737373', fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>Show this QR at the front desk when you arrive.</p>
        </div>

        {/* Details */}
        <div>
          <div style={{ ...row, borderTop: 'none', paddingTop: 0 }}>
            <Receipt className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />
            <div style={{ flex: 1 }}>
              <div style={label}>Reference</div>
              <div style={{ ...value, fontFamily: 'JetBrains Mono, monospace', fontSize: 14 }} data-testid="pay-return-ref">{receipt?.ref_number}</div>
            </div>
          </div>
          <div style={row}>
            {view?.detailIcon}
            <div style={{ flex: 1 }}>
              <div style={label}>{view?.detailLabel}</div>
              <div style={value}>{view?.detailValue || '—'}</div>
            </div>
          </div>
          {view?.secondaryValue && (
            <div style={row}>
              {view.secondaryIcon}
              <div style={{ flex: 1 }}>
                <div style={label}>{view.secondaryLabel}</div>
                <div style={value}>{view.secondaryValue}</div>
              </div>
            </div>
          )}
          {receipt?.date && (
            <div style={row}>
              <Calendar className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />
              <div style={{ flex: 1 }}>
                <div style={label}>Date</div>
                <div style={value}>{fmtDate(receipt.date)}</div>
              </div>
            </div>
          )}
          {(receipt?.start_time || receipt?.end_time) && (
            <div style={row}>
              <Clock className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />
              <div style={{ flex: 1 }}>
                <div style={label}>Time</div>
                <div style={value}>{fmtTime(receipt.start_time)}{receipt.end_time ? ` — ${fmtTime(receipt.end_time)}` : ''}</div>
              </div>
            </div>
          )}
          <div style={row}>
            <Trophy className="h-4 w-4 shrink-0" style={{ color: '#E63946', marginTop: 3 }} />
            <div style={{ flex: 1 }}>
              <div style={label}>Amount paid</div>
              <div style={value}>
                {fmtPHP(receipt?.amount_paid)}
                {isDeposit && Number(receipt?.balance_due) > 0 && (
                  <span style={{ color: '#FFB300', fontSize: 12, marginLeft: 8, fontWeight: 600 }}>
                    · balance {fmtPHP(receipt?.balance_due)} due on arrival
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          {calUrl && (
            <a href={calUrl} target="_blank" rel="noreferrer" data-testid="pay-return-calendar-btn" style={btnGhost}>
              <CalendarPlus className="h-4 w-4" /> Add to Google Calendar
            </a>
          )}
          <button onClick={copyCheckin} data-testid="pay-return-copy-checkin-btn" style={btnGhost}>
            <Copy className="h-4 w-4" /> Copy check-in link
          </button>
        </div>
        <button onClick={() => navigate(staffBack ? staffBack.to : `/book/${slug}`)} data-testid="pay-return-home-btn" style={{ ...btnPrimary, width: '100%', marginTop: 12 }}>
          {staffBack ? staffBack.label
            : type === 'tournament' ? 'Back to facility' : type === 'open_play' ? 'Back to open play' : 'Book another slot'}
        </button>

        <p style={{ color: '#555', fontSize: 11, textAlign: 'center', marginTop: 18, lineHeight: 1.6 }}>
          A confirmation email with your ref number + QR was sent. Screenshot this page as a backup.
        </p>
      </div>
    </div>
  );
}
